import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplate, FormTemplateSection, SipSettingUpdateCallback,
} from 'data/template/template.types';
import * as types from 'data/sipSetting/sipSetting.types';
import Form, {
  FieldUpdateHandlerParam,
} from 'shared/form';
import { AppToaster, showError } from 'shared/toaster';
import { FormLayout } from 'components/FormLayout';
import SipPrefixListForm from '../SipPrefixListForm';
import './sip-setting-form.scss';
import { NetworkElementsListForm } from '../NetworkElementsListForm/NetworkElementsListForm';

type Props = {
  template: FormTemplate,
  data: Partial<types.SipSetting>
  i18nKeyPrefix?: string
  isEditing?: boolean,
  onFieldUpdate?: (param: FieldUpdateHandlerParam, formSection: types.SipSettingSection) => void
  onFieldCreateNew?: any
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: (keepOperation: boolean) => types.SipSetting
}

export const SIP_SETTING_I18N_KEY = 'sipSetting.form';

export const SipSettingForm = forwardRef<Ref, Props>(({
  template,
  data,
  isEditing,
  i18nKeyPrefix = SIP_SETTING_I18N_KEY,
  onFieldUpdate = () => null,
  onFieldCreateNew,
}: Props, ref) => {
  const { t } = useTranslation();
  const sipInfoRef = useRef<Form>(null);
  const sipTimerRef = useRef<Form>(null);
  const sipPrefixListRef = useRef<Form>(null);
  const sipNetElementsRef = useRef<Form>(null);
  const [sipPrefixes, setSipPrefixes] = useState([])

  const isValid = (forceValidation = false, forceTouched = true) => {
    const isSipInfoValid = sipInfoRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSipTimerValid = sipTimerRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSipPrefixListValid = sipPrefixListRef.current?.isValid(forceValidation, forceTouched) ?? true;
    const isSipNetElementsValid = sipNetElementsRef.current?.isValid(forceValidation, forceTouched) ?? true;

    return isSipInfoValid && isSipTimerValid && isSipPrefixListValid && isSipNetElementsValid;
  };

  const getValues = (keepOperation: boolean) => {
    const formValues: types.SipSetting = {
      id: data.id as string || data?.sipInfo?.id as string,
      sipInfo: {
        ...sipInfoRef.current?.getValues(),
      },
      sipTimer: {
        ...sipTimerRef.current?.getValues(),
      },
      prefixes: sipPrefixListRef.current?.getValues(keepOperation),
      networkElements: sipNetElementsRef.current?.getValues(keepOperation),
    };

    return formValues;
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  const onMainFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, types.SipSettingSection.sipInfo);

  const onPrefixFieldUpdate = (param: FieldUpdateHandlerParam) => onFieldUpdate(param, types.SipSettingSection.sipTimer);

  return (
    <div className="sip-setting-form" data-testid="SipSettingForm">
      <div data-testid="SipSettingFormMain">
        <FormLayout>
          <h3 className="sip-setting-form-title">{t('sipSetting.section.sipInformation')}</h3>
          <Form
            ref={sipInfoRef}
            name={template.formId ?? template.key}
            defaults={data.sipInfo}
            fields={(template.sipInfo as FormTemplateSection).fields}
            layout={(template.sipInfo as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={onMainFieldUpdate}
            fieldCreateNewHandler={onFieldCreateNew}
          />
          <h3 className="sip-setting-form-title">{t('sipSetting.section.sipTimer')}</h3>
          <Form
            ref={sipTimerRef}
            name={template.formId ?? template.key}
            defaults={data.sipTimer}
            fields={(template.sipTimer as FormTemplateSection).fields}
            layout={(template.sipTimer as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={onPrefixFieldUpdate}
            fieldCreateNewHandler={onFieldCreateNew}
          />
          <h3 className="sip-setting-form-title">{t('sipSetting.section.sipPrefixList')}</h3>
          <div className="sip-setting-form-prefixes">
            <SipPrefixListForm
              ref={sipPrefixListRef}
              isEditing={isEditing}
              name={template.formId ?? template.key}
              data={data.prefixes || []}
            />
          </div>
          <h3 className="sip-setting-form-title">{t('sipSetting.section.sipNetworksElements')}</h3>
          <NetworkElementsListForm
            ref={sipNetElementsRef}
            isEditing={isEditing}
            data={data.networkElements || []}
            template={template}
            i18nKeyPrefix={i18nKeyPrefix}
          />
        </FormLayout>
      </div>
    </div>
  );
});

export default SipSettingForm;
